// web/src/state/idb.ts

/**
 * Open an IndexedDB database with a one-shot upgrade callback. The wrapper
 * exists so the rest of the offline layer can `await openDb(...)` instead of
 * dealing with raw `IDBOpenDBRequest` events.
 */
export function openDb(
  name: string,
  version: number,
  onUpgrade: (db: IDBDatabase) => void,
): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(name, version);
    req.onupgradeneeded = () => onUpgrade(req.result);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error("indexedDB open failed"));
    req.onblocked = () => reject(new Error("indexedDB open blocked"));
  });
}

/** Promisified wrapper for a single `IDBRequest`. */
export function reqAsPromise<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error("idb request failed"));
  });
}

/** Promisified wait for a transaction to fully commit (or abort). */
export function txDone(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onabort = () => reject(tx.error ?? new Error("idb tx aborted"));
    tx.onerror = () => reject(tx.error ?? new Error("idb tx error"));
  });
}
